// src/Components/Device/WaterLevelSensorPage.jsx
import React, { useEffect, useState } from 'react';
import {
  Table,
  Dropdown,
  ButtonGroup,
  Badge,
} from 'react-bootstrap';
import {
  BsThreeDotsVertical,
  BsClock,
  BsFillGearFill,
  BsBellFill,
  BsTrashFill,
  BsBarChartLineFill,
  BsExclamationCircleFill,
} from 'react-icons/bs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';

import WaterLevelSensorGraph from './WaterLevelSensorGraph';
import DeleteConfirmDialog from './DeleteConfirmDialog';

dayjs.extend(relativeTime);
dayjs.extend(duration);

/* ──────────────────────────────────────────────────────────── */
/*  Re‑usable tiny component: fancy water‑level bar with sx    */
/* ──────────────────────────────────────────────────────────── */
const WaterLevelBar = ({ level = 0, low, high }) => {
  const safe = Math.min(level, 100);
  const overflow = level > 100 ? level - 100 : 0;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Numeric label above bar */}
      <Box
        sx={{
          position: 'relative',
          height: 40,
          width: '100%',
          bgcolor: '#f0f2f5',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 1px 4px rgba(13, 202, 240, 0.08)',
        }}
      >
        {/* Main fill */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: `${safe}%`,
            background: 'linear-gradient(90deg, #0dcaf0 0%, #0d6efd 100%)',
            transition: 'width .4s ease',
            borderRadius: '2px 0 0 2px',
          }}
        />
        {/* Overflow fill */}
        {overflow > 0 && (
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: `${Math.min(overflow, 100)}%`,
              background: 'linear-gradient(90deg, #0d6efd 0%, #6610f2 100%)',
              opacity: 0.5,
              transition: 'width .4s ease',
              borderRadius: '0 2px 2px 0',
            }}
          />
        )}
        {/* Threshold markers as simple lines */}
        {typeof low === 'number' && (
          <Box
            sx={{
              position: 'absolute',
              left: `${low}%`,
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: '#dc3545', // red
              zIndex: 2,
              borderRadius: 1,
            }}
          />
        )}
        {typeof high === 'number' && (
          <Box
            sx={{
              position: 'absolute',
              left: `${high}%`,
              top: 0,
              bottom: 0,
              width: 2,
              bgcolor: '#198754', // green
              zIndex: 2,
              borderRadius: 1,
            }}
          />
        )}
      </Box>
    </Box>
  );
};

/* ──────────────────────────────────────────────────────────── */
/*  Main component                                             */
/* ──────────────────────────────────────────────────────────── */
const WaterLevelSensorPage = ({
  sensorsData,
  onOpenFunctionalDialog,
  onOpenAlarmDialog,
  refreshSensors,
  currentDevice,
  motorStatusData,
  userPermission,
}) => {
  /* ========== auth / perms ========== */
  const [localPerm, setLocalPerm] = useState('');
  const [localRole, setLocalRole] = useState('');
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const d = jwtDecode(token);
      setLocalPerm(d.permission);
      setLocalRole(d.userType);
    } catch (e) {
      console.error('JWT decode error', e);
    }
  }, []);
  const canEdit = userPermission === 'edit' || localPerm === 'edit';
  const isAdmin = localRole === 'Admin';

  /* ========== state ========== */
  const [menuRowId, setMenuRowId] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState(null);
  const [showDeleteDlg, setShowDeleteDlg] = useState(false);
  const navigate = useNavigate();

  /* ========== helpers ========== */
  const valueFmt = (v, unit, f) =>
    (f ? (v / 100) * f : v).toFixed(unit === 'Liters' ? 1 : 2);
  const timeAgo = (ts) => {
    const diffMin = dayjs().diff(dayjs(ts), 'minute');
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return diffMin ? `${h ? h + ' Hr ' : ''}${m} min ago` : 'just now';
  };

  /* ========== render ========== */
  const showValve = currentDevice?.valveStatus;
  const rows = sensorsData?.filter((s) => s.sensor_type !== 'Controller') || [];
  const deviceCode = rows[0]?.device_code;

  return (
    <>
      {/* =================== TABLE =================== */}
      <div className="table-responsive">
      <Table bordered hover size="sm" className="align-middle gy-1">
        <thead className="table-light">
          <tr>
            <th style={{ minWidth: 160 }}>Name</th>
            <th className="text-center" style={{ minWidth: 260 }}>Present Data</th>
            {showValve && <th className="text-center">Valve</th>}
            {canEdit && <th className="text-center">Actions</th>}
          </tr>
        </thead>

        <tbody>
          {rows.map((sensor) => {
            const main = sensor.waterLevelSensorData?.mainData;
            const errors = sensor.waterLevelSensorData?.errors?.activeErrors || [];
            const level = main?.presentLevel ?? 0;
            const fp = sensor.waterLevelSensorData?.functionalPoints || {};
            const low  = typeof fp.lowLevel  === 'number' ? fp.lowLevel  : null;
            const high = typeof fp.highLevel === 'number' ? fp.highLevel : null;

            return (
              <tr key={sensor._id}>
                {/* ==== NAME ==== */}
                <td>
                  <div className="fw-bold">{sensor.sensor_name}</div>
                </td>

                {/* ==== PRESENT DATA ==== */}
                <td>
                  <WaterLevelBar level={level} low={low} high={high} />

                  <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                    <span className="fw-bold">
                      {valueFmt(level, sensor.unit, sensor.factor)} {sensor.unit}
                    </span>

                    {main?.lastPresentValue && (
                      <span className="d-flex align-items-center text-muted small">
                        <BsClock className="me-1" /> {timeAgo(main.lastPresentValue)}
                      </span>
                    )}

                    {!!errors.length && (
                      <span className="d-flex align-items-center text-danger small">
                        <BsExclamationCircleFill className="me-1" />
                        {errors
                          .map((e) => e.replace(/([a-z])([A-Z])/g, '$1 $2'))
                          .join(', ')}
                      </span>
                    )}
                  </div>
                </td>

                {/* ==== VALVE ==== */}
                {showValve && (
                  <td className="text-center">
                    {main?.valveStatus === 'open' && (
                      <Badge bg="success" className="d-inline-flex align-items-center gap-1">
                        Open
                      </Badge>
                    )}
                  </td>
                )}

                {/* ==== ACTIONS ==== */}
                {canEdit && (
                  <td className="text-center">
                    <Dropdown
                      as={ButtonGroup}
                      show={menuRowId === sensor._id}
                      onToggle={(open) => !open && setMenuRowId(null)}
                    >
                      <Dropdown.Toggle
                        variant="link"
                        className="shadow-none text-dark p-0"
                        onClick={() => {
                          setMenuRowId(sensor._id);
                          setSelectedSensor(sensor);
                        }}
                      >
                        <BsThreeDotsVertical />
                      </Dropdown.Toggle>

                      <Dropdown.Menu align="end">
                        {isAdmin && (
                          <Dropdown.Item
                            onClick={() => {
                              setShowDeleteDlg(true);
                              setMenuRowId(null);
                            }}
                          >
                            <BsTrashFill className="me-2" /> Delete
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item
                          onClick={() => {
                            onOpenFunctionalDialog(sensor);
                            setMenuRowId(null);
                          }}
                        >
                          <BsFillGearFill className="me-2" /> Functional Settings
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            onOpenAlarmDialog(sensor);
                            setMenuRowId(null);
                          }}
                        >
                          <BsBellFill className="me-2" /> Alarm Settings
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => {
                            navigate(
                              `/historygraph/waterlevelsensor/${sensor.device_code}/${sensor.sensor_sub_id}`
                            );
                            setMenuRowId(null);
                          }}
                        >
                          <BsBarChartLineFill className="me-2" /> View History
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </Table>
      </div>

      {/* =================== DIALOGS =================== */}
      {selectedSensor && (
        <DeleteConfirmDialog
          open={showDeleteDlg}
          handleClose={() => {
            setShowDeleteDlg(false);
            setSelectedSensor(null);
          }}
          sensor={selectedSensor}
          refreshSensors={refreshSensors}
        />
      )}

      {/* =================== GRAPHS =================== */}
      {deviceCode && (
        <WaterLevelSensorGraph
          key={deviceCode}
          deviceCode={deviceCode}
          currentDevice={currentDevice}
          sensordata={sensorsData}
        />
      )}
    </>
  );
};

export default WaterLevelSensorPage;
